import { createContext, useContext, useEffect, useState, useMemo, useCallback, ReactNode } from "react"
import { useAuthContext } from "../auth/AuthProvider"
import { fetchFeatureFlags, FeatureFlagName, FeatureFlags } from "./featureFlags"

interface FeatureFlagContextValue {
  flags: FeatureFlags
  overrides: FeatureFlags
  isEnabled: (flag: FeatureFlagName) => boolean
  toggle: (flag: FeatureFlagName, enabled: boolean) => void
  removeOverride: (flag: FeatureFlagName) => void
  refreshFlags: () => Promise<FeatureFlags>
}

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  flags: {},
  overrides: {},
  isEnabled: () => false,
  toggle: () => {},
  removeOverride: () => {},
  refreshFlags: async () => ({}),
})

export const useFlags = () => useContext(FeatureFlagContext)

const OVERRIDES_KEY = "featureFlagOverrides"

function loadOverrides(): FeatureFlags {
  try {
    const stored = localStorage.getItem(OVERRIDES_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const [uid] = useAuthContext()
  const [flags, setFlags] = useState<FeatureFlags>({})
  const [overrides, setOverrides] = useState<FeatureFlags>(loadOverrides)

  const refreshFlags = useCallback(async () => {
    const updated = await fetchFeatureFlags()
    setFlags(updated)
    return updated
  }, [])

  useEffect(() => {
    if (!uid) {
      setFlags({})
      return
    }
    refreshFlags()
      .catch(() => setFlags({}))
  }, [uid, refreshFlags])

  useEffect(() => {
    localStorage.setItem(OVERRIDES_KEY, JSON.stringify(overrides))
  }, [overrides])

  const toggle = useCallback((flag: FeatureFlagName, enabled: boolean) => {
    setOverrides(prev => ({ ...prev, [flag]: enabled }))
  }, [])

  const removeOverride = useCallback((flag: FeatureFlagName) => {
    setOverrides(prev => {
      const next = { ...prev }
      delete next[flag]
      return next
    })
  }, [])

  const value = useMemo<FeatureFlagContextValue>(() => ({
    flags,
    overrides,
    isEnabled: (flag: FeatureFlagName) => flag in overrides ? overrides[flag] === true : flags[flag] === true,
    toggle,
    removeOverride,
    refreshFlags,
  }), [flags, overrides, toggle, removeOverride, refreshFlags])

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  )
}
